/**
 * Formatting for the Wave-0 payment strings (`checkout-messages.ts` formats nothing by design).
 * §0: no raw `Date.toString()`, never `undefined`, no elapsed-time suffix.
 */

/** "Nov 24, 2026" in UTC; '' for anything unparseable (callers hide an empty date, never print it). */
export function longDate(value: string | Date | null | undefined): string {
    if (value == null || value === '') return '';
    try {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'});
    } catch {
        return '';
    }
}

/** True when `value` is a real date strictly in the future. */
export function isFuture(value: string | Date | null | undefined, now: number = Date.now()): boolean {
    if (value == null || value === '') return false;
    const d = value instanceof Date ? value : new Date(value);
    return !Number.isNaN(d.getTime()) && d.getTime() > now;
}

/** "$9.99" / "€9.99" from cents; '' when cents is not a finite number. Negative cents are shown as a positive credit. */
export function money(cents: number | null | undefined, currency: string = 'usd'): string {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
    const abs = Math.abs(cents) / 100;
    try {
        return new Intl.NumberFormat('en-US', {style: 'currency', currency: (currency || 'usd').toUpperCase()}).format(abs);
    } catch {
        return `$${abs.toFixed(2)}`;
    }
}

/** "$9.99/mo" / "$99.99/yr" for the plan card. */
export function priceLabel(cents: number | null | undefined, interval: 'MONTHLY' | 'ANNUAL' | string | null | undefined): string {
    const m = money(cents);
    if (!m) return '';
    return `${m}${interval === 'ANNUAL' ? '/yr' : '/mo'}`;
}

/**
 * W0-1 step 2: one UUID per submit (reused only for the network retry of that same submit). The server turns it into
 * the Stripe idempotency key `pay-{attemptId}` / the `PayPal-Request-Id`, so a double submit can never create two
 * provider objects. RFC 4122 v4; falls back to `getRandomValues` where `randomUUID` is missing (older WebViews).
 */
export function newAttemptId(): string {
    const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') {
        c.getRandomValues(bytes);
    } else {
        for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Human plan label from the server's plan vocabulary (`backer_monthly`, `PATRON`, `patron_yearly`…). */
export function planLabel(planName: string | null | undefined): string {
    if (!planName) return '';
    // MON-7: a display label from the server ("Pro Monthly", "Basic") passes through untouched; only the known
    // storage vocabularies are mapped — the constant (PACKAGE_TYPE_PRO / PACKAGE_TYPE_BASIC) and the raw plan names
    // (patron_monthly, backer_yearly, PATRON …) must never reach the screen verbatim.
    const p = planName.toLowerCase().replace(/^package_type_/, '');
    const m = /^(patron|pro|backer|basic)(?:_(monthly|yearly|annual))?$/.exec(p);
    if (!m) return planName;
    const tier = m[1] === 'patron' || m[1] === 'pro' ? 'Pro' : 'Basic';
    if (m[2] === 'yearly' || m[2] === 'annual') return `${tier} Yearly`;
    if (m[2] === 'monthly') return `${tier} Monthly`;
    return tier;
}

/** "Stripe" / "PayPal" for a provider constant; the raw value for anything else. */
export function providerLabel(provider: string | null | undefined): string {
    if (!provider) return '';
    const p = provider.toUpperCase();
    return p === 'STRIPE' ? 'Stripe' : p === 'PAYPAL' ? 'PayPal' : provider;
}
